// Port of certify-ipta/src/lib/scarcity.ts — the "X / N claimed" badges on the bonus cards.
//
// The count is an evergreen time curve, not real inventory: it is a pure function of the clock and
// the slot config. Keep this file's logic identical to certify's and pass the same config, and a
// visitor sees the same number on the certify pages and inside the LMS at the same moment.
// If you change the curve, change it in both repos.

export type BonusClaimedColor = 'green' | 'yellow' | 'red';

export interface ScarcityCurveSegment {
  endHour: number;
  minFraction: number;
  maxFraction: number;
}

const HOURS_PER_DAY = 24;

export const LIVE_BONUS_ANCHOR_UTC = new Date('2026-03-14T10:00:00Z');

export const SLOT_BADGE_DEFAULT_MAX_UNITS = 50;
export const SLOT_BADGE_DEFAULT_ACTIVE_DAYS = 3;
export const SLOT_BADGE_DEFAULT_MAX_CLAIMED = 49;

/**
 * The curve is expressed as a FRACTION of the cohort, not an absolute count, so every cohort size
 * traverses the same shape instead of slamming into its own ceiling. A 50-slot cohort reads 21 -> 49.
 */
export const SLOT_BADGE_CURVE: ReadonlyArray<ScarcityCurveSegment> = [
  { endHour: 6, minFraction: 0.42, maxFraction: 0.6 },
  { endHour: 18, minFraction: 0.6, maxFraction: 0.72 },
  { endHour: 36, minFraction: 0.72, maxFraction: 0.84 },
  { endHour: 60, minFraction: 0.84, maxFraction: 0.92 },
  { endHour: 72, minFraction: 0.92, maxFraction: 0.98 },
];

/** Meso's cohort — same values as certify's MESO_SLOTS. */
export const MESO_SLOTS = {
  maxUnits: 25,
  delayHours: 84,
  cooldownHours: 12,
} as const;

/** Proportional thresholds: green means plenty left, red means nearly gone. */
export function getSlotBadgeColor(claimedUnits: number, maxUnits?: number): BonusClaimedColor {
  if (!maxUnits) {
    if (claimedUnits <= 36) return 'green';
    if (claimedUnits <= 45) return 'yellow';
    return 'red';
  }
  const ratio = claimedUnits / maxUnits;
  if (ratio < 0.65) return 'green';
  if (ratio < 0.9) return 'yellow';
  return 'red';
}

export function getCycleElapsedHours(
  anchorDate: Date,
  currentDate: Date,
  delayHours: number,
  cycleSpanDays: number,
): number {
  const msElapsed = currentDate.getTime() - anchorDate.getTime();
  const baseHours = msElapsed / (1000 * 60 * 60);
  const shifted = baseHours + delayHours;
  const cycleSpanHours = cycleSpanDays * HOURS_PER_DAY;
  const cycle = shifted % cycleSpanHours;
  return cycle < 0 ? cycle + cycleSpanHours : cycle;
}

export function calculateSegmentedCount(
  elapsedHours: number,
  units: number,
  maxCap: number,
  curve: ReadonlyArray<ScarcityCurveSegment> = SLOT_BADGE_CURVE,
): number {
  const at = (fraction: number) => Math.round(fraction * units);
  let previousEnd = 0;
  for (const segment of curve) {
    if (elapsedHours <= segment.endHour) {
      const segmentDuration = Math.max(segment.endHour - previousEnd, 1);
      const segmentProgress = (elapsedHours - previousEnd) / segmentDuration;
      const lo = at(segment.minFraction);
      const hi = at(segment.maxFraction);
      const interpolated = Math.round(lo + segmentProgress * (hi - lo));
      return Math.min(Math.max(interpolated, lo), hi, maxCap);
    }
    previousEnd = segment.endHour;
  }
  return Math.min(at(curve[curve.length - 1].maxFraction), maxCap);
}

export interface SlotBadgeConfig {
  maxUnits?: number;
  finalLowUnitsThreshold?: number;
  delayHours?: number;
  /** Hours of quiet between one cohort filling and the next opening, so the count never visibly
   *  falls backwards. */
  cooldownHours?: number;
  /** What the neutral pill says while cooling down. */
  cooldownLabel?: string;
}

export interface SlotBadgeResult {
  text: string;
  color: BonusClaimedColor;
  /** True during the cooldown window: render a neutral pill with no count. */
  quiet?: boolean;
}

export const SLOT_BADGE_DEFAULT_COOLDOWN_LABEL = 'Included';

export function getSlotBadge(
  {
    maxUnits,
    finalLowUnitsThreshold,
    delayHours = 0,
    cooldownHours = 0,
    cooldownLabel = SLOT_BADGE_DEFAULT_COOLDOWN_LABEL,
  }: SlotBadgeConfig,
  now: Date = new Date(),
): SlotBadgeResult {
  const units = maxUnits ?? SLOT_BADGE_DEFAULT_MAX_UNITS;
  // Cap is "one short of sold out", proportional like the curve.
  const maxClaimed = Math.max(units - 1, 1);
  const lowUnitsThreshold = finalLowUnitsThreshold ?? Math.max(units - 4, 1);

  // The active climb plus the quiet window is one full cycle.
  const activeHours = SLOT_BADGE_DEFAULT_ACTIVE_DAYS * HOURS_PER_DAY;
  const cycleDays = (activeHours + cooldownHours) / HOURS_PER_DAY;

  const elapsedHours = getCycleElapsedHours(LIVE_BONUS_ANCHOR_UTC, now, delayHours, cycleDays);

  if (cooldownHours > 0 && elapsedHours > activeHours) {
    return { text: cooldownLabel, color: 'green', quiet: true };
  }

  const claimedUnits = calculateSegmentedCount(Math.min(elapsedHours, activeHours), units, maxClaimed);

  if (claimedUnits >= lowUnitsThreshold) {
    return { text: `Only ${units - claimedUnits} left`, color: 'red' };
  }

  return {
    text: `${claimedUnits} / ${units} claimed`,
    color: getSlotBadgeColor(claimedUnits, units),
  };
}
